/* eslint-disable import/no-cycle */
import React, {
  useCallback, useEffect, useState,
} from 'react';
import PropTypes from 'prop-types';
import isEmpty from 'lodash/isEmpty';
import pick from 'lodash/pick';
import {
  useDispatch, useSelector, shallowEqual,
} from 'react-redux';
import cx from 'classnames';
import Box from '@material-ui/core/Box';
import { useSteps } from 'components/QuestionnaireSidebar/hooks/useSteps';
import { QuestionnaireSidebar } from 'components/QuestionnaireSidebar/QuestionnaireSidebar';
import { useTranslation } from 'utils/modifiedTranslation';
import { MONTH_PICKER } from 'modules/questionnaire/questionTypes';
import { setSidebarVisibility } from '../../actions';
import { checkQuestionAnswer } from '../../functions/checkQuestionAnswer';
import QuestionTitle from '../QuestionTitle';
import QuestionSubTitle from '../QuestionSubTitle';
import { useTreatmentHistoryStyles } from './styles/styles';
import { SubQuestion } from './SubQuestion';
import { AddNewAnswer } from './AddNewAnswer';
import { QuestionIntro } from './QuestionIntro';
import { CompoundQuestionAnswer } from './CompoundQuestionAnswer';

export const MultipleCompoundQuestion = ({
  question,
  value,
  onChange,
  handleNextButtonClick,
}) => {
  const classes = useTreatmentHistoryStyles();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { questionnaire } = useSelector((state) => ({
    questionnaire: state.questionnaire,
  }), shallowEqual);

  const [currentCompoundQuestionIndex, setCurrentCompoundQuestionIndex] = useState(null);
  const subQuestions = question.subQuestions.filter((q) => q.isShown || q.isShown === undefined);

  const { isSidebarOpen } = questionnaire;

  const {
    activeStep, handleNext, handleBack, handleReset: handleStepsReset, isLastStep,
  } = useSteps(subQuestions.length);

  const currentSubQuestion = currentCompoundQuestionIndex === null ? null : subQuestions[activeStep];
  const currentSubQuestionId = currentSubQuestion?.id;

  const currentCompoundQuestionValue = value?.[currentCompoundQuestionIndex];

  const isCurrentCompoundQuestionAllRequiredSubQuestionsAnswered = !!currentCompoundQuestionValue
    && !question.requiredSubQuestionsId.some((id) => (
      isEmpty(currentCompoundQuestionValue[id])
    ));

  const next = useCallback(() => {
    if (isLastStep) {
      handleNextButtonClick();
    } else {
      handleNext();
    }
  }, [handleNext, handleNextButtonClick, isLastStep]);

  const onSubQuestionChange = useCallback((subQuestionValue) => {
    if (!currentSubQuestionId) {
      // invariant
      return;
    }

    const nextValue = [...(value ?? [])];

    nextValue[currentCompoundQuestionIndex] = {
      ...(nextValue[currentCompoundQuestionIndex] ?? {}),
      [currentSubQuestionId]: subQuestionValue,
    };
    onChange(nextValue);
    if (currentSubQuestion.type === MONTH_PICKER) {
      next();
    }
    // eslint-disable-next-line
  }, [onChange, value, currentSubQuestionId, currentCompoundQuestionIndex, currentSubQuestion, next]);

  const resetAnswer = useCallback(() => {
    if (!currentSubQuestionId) {
      // invariant
      return;
    }

    const nextValue = [...(value ?? [])];

    nextValue[currentCompoundQuestionIndex] = {
      ...(nextValue[currentCompoundQuestionIndex] ?? {}),
      [currentSubQuestionId]: null,
    };
    onChange(nextValue);
  }, [onChange, value, currentSubQuestionId, currentCompoundQuestionIndex]);

  const handleRemoveAnswer = useCallback((questionIndex) => {
    const nextValue = (value ?? []).filter((i, index) => index !== questionIndex);
    onChange(nextValue);
  }, [value, onChange]);

  const handleSidebarClose = useCallback(() => {
    if (!isCurrentCompoundQuestionAllRequiredSubQuestionsAnswered) {
      handleRemoveAnswer(currentCompoundQuestionIndex);
    }
    dispatch(setSidebarVisibility(false));
    setCurrentCompoundQuestionIndex(null);
  },
  [
    currentCompoundQuestionIndex,
    dispatch,
    handleRemoveAnswer,
    isCurrentCompoundQuestionAllRequiredSubQuestionsAnswered,
  ]);

  const handleAddNewAnswer = useCallback(() => {
    handleStepsReset();
    setCurrentCompoundQuestionIndex((value ?? []).length);
    dispatch(setSidebarVisibility(true));
  }, [dispatch, handleStepsReset, value]);

  const handleEditAnswer = useCallback((questionIndex) => {
    handleStepsReset();
    setCurrentCompoundQuestionIndex(questionIndex);
    dispatch(setSidebarVisibility(true));
  }, [dispatch, handleStepsReset]);

  useEffect(() => {
    // on mount only
    if (value && Array.isArray(value) && value.length) {
      return;
    }
    handleAddNewAnswer();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isQuestionAnswered = !!currentSubQuestion && checkQuestionAnswer(
    currentSubQuestion,
    {
      ...pick(questionnaire, ['files', 'geoLocation']),
      form: currentCompoundQuestionValue ?? {},
    },
  );

  const shouldShowSubQuestionNextButton = !!currentSubQuestionId
    && (
      !question.requiredSubQuestionsId.includes(currentSubQuestionId)
      || isCurrentCompoundQuestionAllRequiredSubQuestionsAnswered
    )
    && isQuestionAnswered;

  return (
    <>
      <QuestionnaireSidebar
        isOpen={isSidebarOpen}
        handleClose={handleSidebarClose}
        isLastStep={isLastStep}
        stepsCount={subQuestions.length}
        isShowNext={shouldShowSubQuestionNextButton}
        activeStep={activeStep}
        handleNext={next}
        handleBack={handleBack}
      >
        {!!currentSubQuestion && (
          <SubQuestion
            value={currentCompoundQuestionValue?.[currentSubQuestionId]}
            onChange={onSubQuestionChange}
            question={currentSubQuestion}
            handleCancelClick={handleSidebarClose}
            isCancelButtonShown={false}
            resetAnswer={resetAnswer}
            isResetAnswerShown={!!currentSubQuestion.answerResetable && isQuestionAnswered}
            shouldNextButtonBeShown={shouldShowSubQuestionNextButton}
            handleNextButtonClick={next}
          />
        )}
      </QuestionnaireSidebar>

      <Box>
        {isEmpty(value) ? (
          <QuestionIntro question={question} />
        ) : (
          <QuestionTitle label={t(`questionnaire.questions.your_${question.id}`)} />
        )}
        <Box className={cx(classes.button, { [classes.alignedButton]: !isEmpty(value) })}>
          <AddNewAnswer
            onClick={handleAddNewAnswer}
            label={question.submitButtonLabel}
          />
        </Box>
        {isEmpty(value) ? (
          <QuestionSubTitle label={question.footer} />
        ) : (
          value.map((subQuestionsAnswers, index) => (
            <CompoundQuestionAnswer
              key={index}
              subQuestions={subQuestions}
              subQuestionsAnswers={subQuestionsAnswers}
              handleEdit={() => handleEditAnswer(index)}
              handleRemove={() => handleRemoveAnswer(index)}
            />
          ))
        )}
      </Box>
    </>
  );
};

MultipleCompoundQuestion.propTypes = {
  question: PropTypes.object.isRequired,
  onChange: PropTypes.func.isRequired,
  handleNextButtonClick: PropTypes.func.isRequired,
  value: PropTypes.arrayOf(PropTypes.object),
};
